import { useState } from 'react';
import type {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Input, theme, Tooltip, Typography } from 'antd';
import isNil from 'lodash/isNil';

type UKPostCodeInputProps<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  fieldName: FieldPath<TFieldValues>;
};

export const UKPostCodeInput = <
  TFieldValues extends FieldValues = FieldValues,
>({
  form,
  fieldName,
}: UKPostCodeInputProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { setValue, setError, watch } = form;
  const postcodeValue = watch(fieldName);
  const [inputValue, setInputValue] = useState<string>(postcodeValue ?? '');
  const [userError, setUserError] = useState<boolean>(false);
  const UKPostcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/;

  /* 
    Format post code to have the space between outcode
    and incode as well as upper case
  */
  const formatPostCode = (postCode: string) => {
    let formattedPostCode = postCode;

    if (formattedPostCode.indexOf(' ') === -1) {
      formattedPostCode = formattedPostCode.replace(/^(.*)(\d)/, '$1 $2');
    }

    return formattedPostCode.toUpperCase().trim();
  };

  const onUKPostCodeChange = (postCode: string) => {
    const postCodeHasValue = !isNil(postCode);
    const formattedPostCode = postCodeHasValue ? formatPostCode(postCode) : '';
    const isValidUKPostcodeFormat =
      postCodeHasValue && UKPostcodeRegex.test(formattedPostCode);

    // Can't rely on setError as setValue clears custom errors when shouldValidate
    // is set to true which is required or when editing an address it won't let the user update
    if (postCode.trim() !== '' && !isValidUKPostcodeFormat) {
      setUserError(true);
      setError(fieldName, {
        type: 'custom',
        message: 'Invalid Format',
      });
    } else {
      setUserError(false);
      setValue(
        fieldName,
        formattedPostCode as PathValue<TFieldValues, Path<TFieldValues>>,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    }

    setInputValue(postCode);
  };

  return (
    <Form.Item
      label={
        <>
          <Typography.Text style={{ marginRight: token.marginXS }}>
            {t('common.address.postCode')}
          </Typography.Text>
          <Tooltip title={t('common.address.postCodeToolTip')}>
            <FontAwesomeIcon icon={faQuestionCircle} />
          </Tooltip>
        </>
      }
      required
    >
      <>
        <Input
          aria-label={t('common.address.postCode')}
          placeholder={t('common.placeholders.enterField', {
            field: t('common.address.postCode'),
          })}
          onChange={(e) => onUKPostCodeChange(e.target.value)}
          defaultValue={inputValue}
        />
        {!inputValue && (
          <Typography.Text style={{ color: token.colorError }}>
            {t('common.formErrors.required')}
          </Typography.Text>
        )}
        {userError && (
          <Typography.Text style={{ color: token.colorError }}>
            {t('common.address.postCodeCustomError')}
          </Typography.Text>
        )}
      </>
    </Form.Item>
  );
};
